import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import CustomFieldValues from '../util/CustomFieldValues';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editOrg?: any;
}

export default function AddCrmOrganisation({ open, onClose, onSaved, editOrg }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [orgTypes, setOrgTypes] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [orgTypeId, setOrgTypeId] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [companyRegNumber, setCompanyRegNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [charityNumber, setCharityNumber] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('crm/organisation-types');
        setOrgTypes(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (editOrg) {
      setName(editOrg.name ?? '');
      setOrgTypeId(editOrg.organisationTypeId ? String(editOrg.organisationTypeId) : '');
      setIndustry(editOrg.industry ?? '');
      setWebsite(editOrg.website ?? '');
      setEmail(editOrg.email ?? '');
      setPhone1(editOrg.phone1 ?? '');
      setPhone2(editOrg.phone2 ?? '');
      setCompanyRegNumber(editOrg.companyRegNumber ?? '');
      setVatNumber(editOrg.vatNumber ?? '');
      setCharityNumber(editOrg.charityNumber ?? '');
      setAddressLine1(editOrg.addressLine1 ?? '');
      setAddressLine2(editOrg.addressLine2 ?? '');
      setCity(editOrg.city ?? '');
      setPostcode(editOrg.postcode ?? '');
      setNotes(editOrg.notes ?? '');
      setIsActive(editOrg.isActive ?? true);
    } else {
      setName(''); setOrgTypeId(''); setIndustry(''); setWebsite('');
      setEmail(''); setPhone1(''); setPhone2('');
      setCompanyRegNumber(''); setVatNumber(''); setCharityNumber('');
      setAddressLine1(''); setAddressLine2(''); setCity(''); setPostcode('');
      setNotes(''); setIsActive(true);
    }
  }, [editOrg, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar(t('Required'), { variant: 'error' });
      return;
    }
    if (!orgTypeId) {
      enqueueSnackbar('Organisation type is required', { variant: 'error' });
      return;
    }
    const payload: any = {
      name: name.trim(),
      organisationTypeId: Number(orgTypeId),
      industry: industry.trim() || null,
      website: website.trim() || null,
      email: email.trim() || null,
      phone1: phone1.trim() || null,
      phone2: phone2.trim() || null,
      companyRegNumber: companyRegNumber.trim() || null,
      vatNumber: vatNumber.trim() || null,
      charityNumber: charityNumber.trim() || null,
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      city: city.trim() || null,
      postcode: postcode.trim() || null,
      notes: notes.trim() || null,
      isActive,
    };
    try {
      if (editOrg?.id) {
        await axios.put(`crm/organisations/${editOrg.id}`, payload);
        enqueueSnackbar('Organisation updated', { variant: 'success' });
      } else {
        await axios.post('crm/organisations', payload);
        enqueueSnackbar('Organisation created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const field = (label: string, value: string, set: (v: string) => void, type = 'text') => (
    <div className="flex flex-col mb-[8px]">
      <label className="text-[10px] ml-[5px] text-[var(--text-color)]">{label}</label>
      <input
        className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
        type={type}
        value={value}
        onChange={(e) => set(e.target.value)}
      />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520, bgcolor: 'var(--bg-form)',
          border: 'var(--border-input)', borderRadius: '5px',
          boxShadow: 24, p: 3, maxHeight: '90vh', overflowY: 'auto',
        }}>
          <div className="text-[16px] font-semibold mb-[14px] text-[var(--text-color)]">
            {editOrg ? t('Edit') : t('AddOrganisation')}
          </div>

          {field(t('Name'), name, setName)}

          <div className="flex flex-col mb-[8px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">{t('OrganisationType')}</label>
            <select className="edit-select p-[5px] rounded-[5px] edit-input text-[14px]"
              value={orgTypeId} onChange={(e) => setOrgTypeId(e.target.value)}>
              <option value="">—</option>
              {orgTypes.map((ot) => (
                <option key={ot.id} value={ot.id}>{ot.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-x-[10px]">
            {field(t('Email'), email, setEmail, 'email')}
            {field('Website', website, setWebsite)}
          </div>

          <div className="grid grid-cols-2 gap-x-[10px]">
            {field(t('MobilePhone'), phone1, setPhone1)}
            {field(t('WorkPhone'), phone2, setPhone2)}
          </div>

          <hr className="hr my-[10px]" />
          <div className="text-[11px] text-[var(--text-color-calendar-week)] mb-[6px]">Legal</div>

          <div className="grid grid-cols-2 gap-x-[10px]">
            {field(t('CompanyRegNumber'), companyRegNumber, setCompanyRegNumber)}
            {field(t('VatNumber'), vatNumber, setVatNumber)}
          </div>
          {field(t('CharityNumber'), charityNumber, setCharityNumber)}

          <hr className="hr my-[10px]" />
          <div className="text-[11px] text-[var(--text-color-calendar-week)] mb-[6px]">Address</div>

          {field(t('AddressLine1'), addressLine1, setAddressLine1)}
          {field(t('AddressLine2'), addressLine2, setAddressLine2)}
          <div className="grid grid-cols-2 gap-x-[10px]">
            {field(t('City'), city, setCity)}
            {field(t('Postcode'), postcode, setPostcode)}
          </div>

          <div className="flex flex-col mb-[8px]">
            <label className="text-[10px] ml-[5px] text-[var(--text-color)]">{t('Notes')}</label>
            <textarea
              className="w-full text-[14px] py-[4px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Custom fields (edit mode only) */}
          {editOrg?.id && orgTypeId && (
            <CustomFieldValues
              tableName="crm_organisation"
              entityId={editOrg.id}
              typeId={Number(orgTypeId)}
            />
          )}

          <label className="flex items-center gap-[8px] text-[13px] cursor-pointer mb-[16px] text-[var(--text-color)]">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            {t('IsActive')}
          </label>

          <div className="flex gap-[10px]">
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={handleSave}>
              {editOrg ? t('Change') : t('Add')}
            </Button>
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={onClose}>
              {t('Cancel')}
            </Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}
